"use client";

import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import Cookies from "js-cookie";
import { fetchAllCurrencies } from "@/api-service/currency";
import { fetchGeneralSettings } from "@/api-service/general";
import { Currency } from "@/packages/types/currency";

export type { Currency };

interface CurrencyContextType {
  id?: number;
  symbol: string;
  exchangeRate: number;
  code: string;
  currencies: Currency[];
  loadingCurrencies: boolean;
  changeCurrency: (item: Currency) => void;
  isInitialized: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

export const CurrencyProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [id, setId] = useState<number | undefined>();
  const [symbol, setSymbol] = useState<string | undefined>();
  const [exchangeRate, setExchangeRate] = useState<number | undefined>();
  const [code, setCode] = useState<string | undefined>();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState<boolean>(true);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const changeCurrency = useCallback((item: Currency) => {
    if (
      item.code === code &&
      item.symbol === symbol &&
      item.exchangeRate === exchangeRate
    ) {
      return; // Skip duplicate state updates
    }
    localStorage.setItem("currency", JSON.stringify(item));
    Cookies.set("currency", item.code, { expires: 365 });
    setId(item.id);
    setSymbol(item.symbol);
    setExchangeRate(item.exchangeRate);
    setCode(item.code);
    setIsInitialized(true);
  }, [code, symbol, exchangeRate]);

  useEffect(() => {
    let active = true;

    const storedCurrency = localStorage.getItem("currency");
    if (storedCurrency) {
      try {
        const parsed: Currency = JSON.parse(storedCurrency);
        setId(parsed.id);
        setSymbol(parsed.symbol);
        setExchangeRate(parsed.exchangeRate);
        setCode(parsed.code);
      } catch (err) {
        console.error("Invalid currency data in localStorage", err);
      }
    }

    const loadAllCurrencies = async () => {
      try {
        const [currenciesRes, settingsRes] = await Promise.all([
          fetchAllCurrencies(),
          fetchGeneralSettings().catch(() => null),
        ]);

        if (active && currenciesRes?.data) {
          setCurrencies(currenciesRes.data);
          if (!storedCurrency) {
            // 1. First priority: Site settings configured currency code
            const siteCurrencyCode = settingsRes?.data?.currency?.currency?.toUpperCase();
            let defaultCurr = currenciesRes.data.find(
              (c) => c.code?.toUpperCase() === siteCurrencyCode
            );

            // 2. Second priority fallback: Currency marked as isDefault
            if (!defaultCurr) {
              defaultCurr = currenciesRes.data.find((c) => c.isDefault);
            }

            // 3. Final fallback: First available currency
            if (!defaultCurr) {
              defaultCurr = currenciesRes.data[0];
            }

            if (defaultCurr) {
              changeCurrency(defaultCurr);
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch currencies in CurrencyContext", err);
      } finally {
        if (active) setLoadingCurrencies(false);
      }
    };

    loadAllCurrencies();
    setIsInitialized(true);

    return () => {
      active = false;
    };
  }, []);

  return (
    <CurrencyContext.Provider
      value={{
        id,
        symbol: symbol ?? "",
        exchangeRate: exchangeRate ?? 1,
        code: code ?? "",
        currencies,
        loadingCurrencies,
        isInitialized,
        changeCurrency,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
  return ctx;
};

